/**
 * Which ONE surface a plain "user clicked Upgrade" opens.
 *
 * Sibling of lib/trial/gate-surface.ts. That file answers the same question for
 * a CREDIT GATE (the 402 path); this one answers it for every deliberate
 * upgrade click — the workspace header button, the mobile sheet, the credit
 * chip, Settings → Billing, the feature gates, the post-build and post-survey
 * CTAs. Until now only the credit gate consulted trial eligibility at all,
 * which is why an eligible free user who pressed "Upgrade plan" was shown
 * full-price Pro and only ever met the $1 offer by running out of credits.
 *
 * Pure, synchronous and total, for the same reason gate-surface.ts is: the
 * eligibility read is prefetched, so the decision resolves without an await. An
 * async check here would open the full-price paywall first and swap it for the
 * trial a beat later — which is the stacked-modal bug as the user experiences
 * it, not a cosmetic delay.
 *
 * This file decides WHICH surface. It does not decide whether the user is
 * eligible — that is settled server-side in lib/trial/eligibility.ts and
 * arrives here as `trialEligible` / `trialOfferPresent`.
 */

export type UpgradeEntrySurface =
  /** Free, no payment method, never trialled → the card-first three-day trial
   *  modal: the SAME surface the build gate opens for `card_required`
   *  (lib/trial/gate-surface.ts). Decided above the legacy offer so one
   *  audience cannot meet two different trials depending on which button they
   *  pressed (PANDA-2375). */
  | "card_first"
  /** Free + eligible → the $1 trial offer, and nothing else. */
  | "trial"
  /** Free (or ineligible) → the plans picker, which IS the compare-plans view. */
  | "choose_plan"
  /** Paid → raise the monthly allowance / buy credits. */
  | "add_credits"
  /** Mid-$1-trial → Billing. Purchases and plan changes are locked until it
   *  converts, so every buy screen is a dead end for these users. */
  | "plan_management"
  /** Grow-mode click → the separate Grow plan picker. Out of scope for the $1
   *  trial, which only ever converts to Build's Pro. */
  | "grow_plans";

export interface UpgradeEntryInput {
  /** Account plan. A $1-trial user is plan='pro', so this alone never
   *  identifies one — see `trialActive`. */
  plan: string | null | undefined;
  /** Whether the user is CURRENTLY inside an active $1 trial. */
  trialActive: boolean;
  /** NEXT_PUBLIC_TRIAL_OFFER_ENABLED. Off → nobody is ever offered the trial. */
  trialFlagEnabled: boolean;
  /** Server-computed eligibility, prefetched before the click. */
  trialEligible: boolean;
  /** Whether a concrete offer object came back with that eligibility. */
  trialOfferPresent: boolean;
  /** The server says this account has no payment method and must set one up
   *  before Panda works for it — the card-first gate's `card_required`, read
   *  from the same eligibility payload rather than re-derived here. */
  cardFirstRequired?: boolean;
  /** The workspace is in Grow mode (a separate subscription ladder). */
  growthMode?: boolean;
  /** Whether the account already has a Grow subscription. */
  hasGrowthSubscription?: boolean;
}

/** Any non-free plan id, including the grandfathered "starter". */
function isPaid(plan: string | null | undefined): boolean {
  return typeof plan === "string" && plan.length > 0 && plan !== "free";
}

/**
 * Resolve the single surface for this upgrade click.
 *
 * Order is the contract, not an implementation detail:
 *   1. Grow mode is a different product with a different ladder. It is decided
 *      first so no Build-plan reasoning can leak into it.
 *   2. An active $1 trial goes to plan management. It must NOT fall through to
 *      add_credits: every purchase route refuses a trialing user
 *      (lib/trial/purchase-guard.ts), so that screen's only button fails.
 *      Checked before the paid branch because a trial user IS plan='pro'.
 *   3. A free account the server has gated on a payment method gets the
 *      card-first trial — before the $1 offer, because these are the same
 *      people: an account with no card that has never trialled would otherwise
 *      meet the legacy offer here and the card-first modal at the build gate,
 *      which is the "competing trial offers" the audit found (PANDA-2375).
 *   4. A free, eligible user gets the $1 offer — at EVERY entry point, which is
 *      the whole point of this file.
 *   5. Any other paid user manages credits.
 *   6. Everyone else — free but ineligible (already trialed, previously paid,
 *      holdout, flag off) — gets the normal plans picker. That screen shows all
 *      three plans at once, so it already satisfies "Upgrade to Pro with a
 *      Compare plans option"; there is no separate compare view to route to.
 */
export function resolveUpgradeEntrySurface(
  input: UpgradeEntryInput,
): UpgradeEntrySurface {
  if (input.growthMode) {
    return input.hasGrowthSubscription ? "plan_management" : "grow_plans";
  }

  if (input.trialActive) return "plan_management";

  // Server-authoritative and ahead of every trial branch below, for the same
  // reason gate-surface.ts short-circuits on it: `card_required` is not a
  // statement about credits or offer windows, it is the account being unable to
  // do the work at all until a payment method exists.
  if (input.cardFirstRequired && !isPaid(input.plan)) return "card_first";

  if (
    !isPaid(input.plan) &&
    input.trialFlagEnabled &&
    input.trialEligible &&
    input.trialOfferPresent
  ) {
    return "trial";
  }

  if (isPaid(input.plan)) return "add_credits";

  return "choose_plan";
}
